import { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Container, Flex } from '@/components'
import { Col, FieldSet, Legend, Row } from '@/components/Layout'
import { Form, SelectFormField, TextFormField } from '@/components/UberForm'
import { FormlessFormField } from '@/components/UberForm/FormField'
import { getAllBusinessDomains, getStereotypes, getSystemUsers } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import {
	ObjectSettingsUpdateDto,
	StereotypeDto,
	StructureObjectDto,
} from '@/endpoints/models'
import { IdCodeName } from '@/endpoints/schemas'
import {
	FolderOdcsFields,
	isOdcsFormField,
	mapOdcsFormPatch,
} from '@/features/folder-odcs/FolderOdcsFields'
import { useAppContext, useCustomAttributesInitValues } from '@/hooks'
import { CustomAttributes } from '@/pages/User/pages/Home/components'
import ObjectSettingsControl from '@/pages/User/pages/Home/components/ObjectSettingsControl'
import {
	FolderData,
	FolderDataForm,
	OpenedFolderData,
} from '@/store/modules/folder/types'
import { GenerateCodeEnum } from '@/types'

type OverviewProps = {
	data: OpenedFolderData
	isEditMode: boolean
	node?: StructureObjectDto
	onChange: (v: Partial<FolderData>) => void
	/** Selected nodes for batch edit */
	selectedNodes?: StructureObjectDto[]
	systemNodeId: number
}

export const Overview = ({
	node,
	isEditMode,
	onChange,
	data,
	systemNodeId,
	selectedNodes,
}: OverviewProps) => {
	const { t } = useAppContext()
	const refForm = useRef<Form<FolderDataForm>>(null)

	const [systemUsers, isSystemUserLoading] = useApi(
		getSystemUsers(systemNodeId),
	)

	const [businessDomains, isBusinessDomainsLoading] = useApi(
		getAllBusinessDomains(),
	)

	const stereotypes = useApi(
		getStereotypes({ type: StereotypeDto.TypeEnum.FOLDER }),
	)

	const {
		initialValuesCustomAttributes,
		parseCustomAttribute,
		customAttributes,
		getCustomAttributesReset,
	} = useCustomAttributesInitValues(
		data.form,
		StructureObjectDto.TypeEnum.FOLDER,
	)

	const handleObjectSettingsChange = useCallback(
		(newValue: IdCodeName[]) => {
			onChange({ objectSettings: newValue })
		},
		[onChange],
	)

	const handleOdcsFieldChange = useCallback(
		(patch: Partial<FolderDataForm>) => {
			onChange(mapOdcsFormPatch(patch))
		},
		[onChange],
	)

	const handleChange = useCallback(
		(
			item: Partial<FolderDataForm>,
			source?: FormlessFormField<FolderDataForm>,
		) => {
			if (source) {
				const name = source.props.name

				if (name === 'ownerId') {
					const owner = systemUsers?.find((u) => u.id === item[name])

					onChange({
						[name]: owner?.id,
						ownerName: owner?.name,
					})

					return
				}

				if (name === 'stereotypeId') {
					const stereotype = stereotypes.data?.find((u) => u.id === item[name])

					const { customAttributesForm, customAttributesRedux } =
						getCustomAttributesReset(stereotype?.id)

					refForm.current?.setValues(customAttributesForm)

					onChange({
						[name]: stereotype?.id,
						stereotypeCode: stereotype?.code,
						customAttributes: customAttributesRedux,
					})

					return
				}

				if (name === 'businessDomainId') {
					const businessDomain = businessDomains?.find(
						(d) => d.id === item[name],
					)

					onChange({
						businessDomainId: businessDomain?.id,
						businessDomainName: businessDomain?.name,
					})

					return
				}

				if (isOdcsFormField(name)) {
					onChange(mapOdcsFormPatch(item))

					return
				}

				onChange(
					parseCustomAttribute(
						{
							[name]: item[name],
						},
						data.form.customAttributes,
						customAttributes,
					),
				)
			}
		},
		[
			onChange,
			parseCustomAttribute,
			data.form.customAttributes,
			systemUsers,
			stereotypes,
			getCustomAttributesReset,
			businessDomains,
		],
	)

	const generateCodeOptions = useMemo(
		() =>
			Object.values(GenerateCodeEnum).map((key) => ({
				value: key,
				label: t(key),
			})),
		[t],
	)

	const preferWorkingData = !isEditMode

	return (
		<Container className="pt-5 h-full overflow-y-auto">
			<Form<FolderDataForm>
				defaultValues={{
					name: node?.name,
					ownerId: data.form.ownerId,
					stereotypeId: data.form.stereotypeId,
					generateCode: data.form.generateCode,
					generateTableColumnCode: data.form.generateTableColumnCode,
					businessDomainId: data.form.businessDomainId,
					odcsStatus: data.form.odcsStatus,
					odcsTenant: data.form.odcsTenant,
					...initialValuesCustomAttributes,
				}}
				onChange={handleChange}
				disabled={isEditMode}
				enableFieldHighlight
				ref={refForm}
			>
				<Flex>
					<TextFormField
						name="name"
						title={t('NAME')}
						disabled={Boolean(selectedNodes)}
						initialValue={data.form.name}
					/>

					<SelectFormField
						name="ownerId"
						title={t('NODE_OWNER')}
						options={systemUsers || []}
						fieldIsLoading={isSystemUserLoading}
						initialValue={data.form.ownerId}
						valueKey="id"
						labelKey="name"
						isNumeric
						allowEmpty
						clearable
						isDisabled={isEditMode}
					/>
					<SelectFormField
						name="stereotypeId"
						title={t('MAPPING_STEREOTYPE')}
						fieldIsLoading={stereotypes.loading}
						initialValue={data.form.stereotypeId}
						options={stereotypes.data || []}
						valueKey="id"
						labelKey="name"
						isNumeric
						allowEmpty
						clearable
						isDisabled={isEditMode}
					/>
				</Flex>
				<Flex>
					<div style={{ width: '200px' }}>
						<SelectFormField
							name="generateCode"
							title={t('GENERATE_ELEMENT_CODE')}
							options={generateCodeOptions}
							initialValue={data.form.generateCode}
							allowEmpty
							clearable
							tooltip={t('UNDEFINED_INHERIT')}
							isDisabled={isEditMode}
						/>
					</div>
					<div style={{ width: '200px' }}>
						<SelectFormField
							name="generateTableColumnCode"
							title={t('GENERATE_TABLE_COLUMN_CODE')}
							options={generateCodeOptions}
							initialValue={data.form.generateTableColumnCode}
							allowEmpty
							clearable
							tooltip={t('UNDEFINED_INHERIT')}
							isDisabled={isEditMode}
						/>
					</div>
				</Flex>

				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_BUSINESS_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SelectFormField
								name="businessDomainId"
								title={t('TABLE_GENERAL_BUSINESS_DOMAIN')}
								initialValue={data.form.businessDomainId}
								options={businessDomains || []}
								fieldIsLoading={isBusinessDomainsLoading}
								valueKey="id"
								labelKey="name"
								isNumeric
								allowEmpty
								isDisabled={isEditMode}
							/>
						</Col>
					</Row>
				</FieldSetGap>

				<FolderOdcsFields
					form={data.form}
					isReadOnly={isEditMode}
					nodeId={node?.id}
					onOdcsFieldChange={handleOdcsFieldChange}
					preferWorkingData={preferWorkingData}
				/>

				<ObjectSettingsControl
					name="objectSettings"
					label={t('OBJECTS')}
					onChange={handleObjectSettingsChange}
					disabled={isEditMode}
					data={data.form.objectSettings}
					type={ObjectSettingsUpdateDto.StructureTypeEnum.FOLDER}
				/>

				<CustomAttributes customAttributes={customAttributes} />
			</Form>
		</Container>
	)
}

const FieldSetGap = styled(FieldSet)`
	margin-top: 1.25rem;
`
